import React from 'react';
import { observer, inject } from 'mobx-react';
import PropTypes from 'prop-types';
import { withNamespaces, Trans } from 'react-i18next';

import { analytics } from 'core/analytics';

import {
  FullWidthDivider,
  Center,
  WarningButton,
  SettingsSection,
} from 'legacyComponents/styled/lib';

import { SectionTitle } from 'apps/group/styles';
import { Banner, Spinner, Paragraph } from 'components';
import { SubscriptionAppManager } from 'apps/payment/subscription/appManager';
import { PlanDescription } from 'apps/payment/subscription/planDescription';
import { PlanDetails } from 'apps/payment/subscription/planDetails';
import { SeatsBreakdown } from 'apps/payment/subscription/seatsBreakdown';
import { EditSeats } from 'apps/payment/subscription/editSeats';
import { Coupon } from 'apps/payment/subscription/coupon';
import { SmallPrint, DowngradeText } from 'apps/payment/styles';
import { isTrialExpired } from 'utils/freeTrials';

@withNamespaces()
@inject('routerStore')
@observer
export class BillingSubscription extends React.Component {
  constructor(props) {
    super(props);
    const {
      routerStore: { params },
    } = props;
    const spaceSlug = params.get('spacePath');
    this.appManager = new SubscriptionAppManager(this, { spaceSlug });
  }

  componentDidMount() {
    const {
      routerStore: { params },
    } = this.props;
    if (params.get('subscribe')) {
      params.set('subscribe', false);
      this.subscribe();
    }
  }

  subscribe = () => {
    const { paymentSpace, paymentSpacePreview } = this.appManager;
    if (!paymentSpace) return;
    analytics.sendEvent('AddToCart', {}, { twitter: true, facebook: true });
    if (
      !paymentSpacePreview ||
      (paymentSpacePreview && !paymentSpacePreview.preview)
    ) {
      this.appManager.NewSubscriptionQuery({ preview: true });
    }
    paymentSpace.ConfirmSubscribe();
  };

  render() {
    const { loading, paymentSpace, paymentSpacePreview } = this.appManager;
    if (loading || !paymentSpace) {
      return (
        <SettingsSection>
          <SectionTitle level={2} size='medium'>
            <Trans>Active Plan</Trans>:
          </SectionTitle>
          <FullWidthDivider />
          <Spinner margin='20px auto' />
        </SettingsSection>
      );
    }

    const { subscription, inSpace } = paymentSpace;
    const trialPeriodEnd = inSpace && inSpace.trialPeriodEnd;

    let callToAction = null;

    /* 
    let upgradePlanName = 'Individual subscription';
    if (spaceType === 'initiative') {
      upgradePlanName = 'Team subscription';
    }
    if (!subscription) {
      let priceDetails = '(€7/month)';
      let info = 'Includes 3 private collaborators.';
      if (spaceType === 'initiative') {
        priceDetails = '(€9/user/month)';
        info =
          'Starts at €25/month, includes your first 5 private collaborators.';
      }
      const article = spaceType === 'initiative' ? 'a' : 'an';
      callToAction = (
        <UpgradeCta>
          <Paragraph>
            <Trans>Get unlimited private projects with {article}</Trans>{' '}
            <strong>
              {upgradePlanName} <Trans>{priceDetails}</Trans>
            </strong>
          </Paragraph>
          <Paragraph>
            <Trans>{info}</Trans>{' '}
            <a
              href='https://wikifactory.com/pricing'
              target='_blank'
              rel='noopener noreferrer'
            >
              <Trans>Learn more</Trans>
            </a>
            .
          </Paragraph>
          <UpgradeCtaButton
            text={<Trans>Subscribe now</Trans>}
            onClick={this.subscribe}
            size='small'
          />
        </UpgradeCta>
      );
    } */
    if (paymentSpace.requiresSCAValidation) {
      callToAction = (
        <Banner variant='warning'>
          <Paragraph>
            <strong>Action Required</strong>
          </Paragraph>
          <Paragraph>
            <Trans>
              In accordance with the European{' '}
              <a
                href='https://en.wikipedia.org/wiki/Strong_customer_authentication'
                target='_blank'
                rel='noopener noreferrer'
              >
                Strong Customer Authentication
              </a>{' '}
              regulation, you will need to{' '}
              <strong>
                validate this month’s payment of your Wikifactory Subscription{' '}
              </strong>
              .
            </Trans>
          </Paragraph>
          <Paragraph>
            Please note that if you fail to do so within the next few days, that
            your Subscription will be cancelled.
          </Paragraph>
          <Center>
            <WarningButton onClick={paymentSpace.ValidateSCA}>
              <Trans>Validate Payment</Trans>
            </WarningButton>
          </Center>
        </Banner>
      );
    }

    return (
      <SettingsSection>
        <SectionTitle level={2} size='medium'>
          <Trans>Active Plan</Trans>:
        </SectionTitle>
        <FullWidthDivider />
        <PlanDescription
          subscription={subscription}
          trialPeriodEnd={trialPeriodEnd}
        />
        <PlanDetails paymentSpace={paymentSpace} />
        {(!trialPeriodEnd || isTrialExpired(trialPeriodEnd)) && (
          <>
            <SeatsBreakdown paymentSpace={paymentSpace} />
            {subscription && !subscription.isCancelled && (
              <EditSeats
                paymentSpace={paymentSpace}
                paymentSpacePreview={paymentSpacePreview}
              />
            )}
            {subscription &&
              !subscription.isGrandFatherPlan &&
              !subscription.isCancelled && (
                <React.Fragment>
                  <FullWidthDivider margin='20px 0px 0px 0px' />
                  <Paragraph>
                    <SmallPrint>
                      <Trans>Don&apos;t need private projects anymore?</Trans>{' '}
                      <DowngradeText onClick={paymentSpace.ConfirmCancel}>
                        <Trans>Downgrade to Wikifactory Free</Trans>
                      </DowngradeText>
                      .
                    </SmallPrint>
                  </Paragraph>
                </React.Fragment>
              )}
          </>
        )}
        {callToAction}
        {callToAction && <Coupon paymentSpace={paymentSpace} />}
      </SettingsSection>
    );
  }
}

BillingSubscription.propTypes = {
  routerStore: PropTypes.object,
};
